import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  Grid,
  Button,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Twitter,
  Instagram,
  LinkedIn,
  Apple as AppleIcon,
  Close,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiService';
import { Translate as TranslateIcon, History as HistoryIcon } from '@mui/icons-material';
import LanguageModal from '../Common/LanguageModal';

interface Language {
  code: string;
  name: string;
  flag: string | any;
  isImage?: boolean;
}

export default function Footer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [safetyModalOpen, setSafetyModalOpen] = useState(false);

  // Content states
  const [termsContent, setTermsContent] = useState('');
  const [termsContentFr, setTermsContentFr] = useState('');
  const [termsTitle, setTermsTitle] = useState('Terms of Service');
  const [termsTitleFr, setTermsTitleFr] = useState('Conditions d\'utilisation');
  const [privacyContent, setPrivacyContent] = useState('');
  const [privacyContentFr, setPrivacyContentFr] = useState('');
  const [privacyTitle, setPrivacyTitle] = useState('Privacy Policy');
  const [privacyTitleFr, setPrivacyTitleFr] = useState('Politique de confidentialité');
  const [loadingContent, setLoadingContent] = useState(false);
  const [langTab, setLangTab] = useState(0);

  // Translation states
  const [targetLang, setTargetLang] = useState<Language>({ code: 'en', name: 'English', flag: '🇺🇸' });
  const [langModalVisible, setLangModalVisible] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslatedTerms, setShowTranslatedTerms] = useState(false);
  const [translatedTermsContent, setTranslatedTermsContent] = useState('');
  const [translatedTermsTitle, setTranslatedTermsTitle] = useState('');
  const [showTranslatedPrivacy, setShowTranslatedPrivacy] = useState(false);
  const [translatedPrivacyContent, setTranslatedPrivacyContent] = useState('');
  const [translatedPrivacyTitle, setTranslatedPrivacyTitle] = useState('');

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  const fetchTermsContent = async () => {
    if (termsContent) return; // Already loaded
    setLoadingContent(true);
    try {
      const response = await axios.get(`${apiUrl}/content/terms`);
      if (response.data.success && response.data.data) {
        setTermsTitle(response.data.data.title || 'Terms of Service');
        setTermsTitleFr(response.data.data.titleFr || 'Conditions d\'utilisation');
        setTermsContent(response.data.data.content || '');
        setTermsContentFr(response.data.data.contentFr || '');
      }
    } catch (err) {
      console.error('Error fetching terms:', err);
    } finally {
      setLoadingContent(false);
    }
  };

  const fetchPrivacyContent = async () => {
    if (privacyContent) return; // Already loaded
    setLoadingContent(true);
    try {
      const response = await axios.get(`${apiUrl}/content/privacy`);
      if (response.data.success && response.data.data) {
        setPrivacyTitle(response.data.data.title || 'Privacy Policy');
        setPrivacyTitleFr(response.data.data.titleFr || 'Politique de confidentialité');
        setPrivacyContent(response.data.data.content || '');
        setPrivacyContentFr(response.data.data.contentFr || '');
      }
    } catch (err) {
      console.error('Error fetching privacy:', err);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleTermsClick = () => {
    setTermsModalOpen(true);
    fetchTermsContent();
  };

  const handlePrivacyClick = () => {
    setPrivacyModalOpen(true);
    fetchPrivacyContent();
  };

  const handleTranslateTerms = async () => {
    if (showTranslatedTerms) {
      setShowTranslatedTerms(false);
      return;
    }
    if (translatedTermsContent) {
      setShowTranslatedTerms(true);
      return;
    }
    try {
      setIsTranslating(true);
      const currentContent = langTab === 0 ? termsContent : termsContentFr;
      const currentTitle = langTab === 0 ? termsTitle : termsTitleFr;
      const [contentRes, titleRes] = await Promise.all([
        apiService.translateText(currentContent, targetLang.code),
        apiService.translateText(currentTitle, targetLang.code)
      ]);
      if (contentRes.success) setTranslatedTermsContent(contentRes.data.translatedText);
      if (titleRes.success) setTranslatedTermsTitle(titleRes.data.translatedText);
      setShowTranslatedTerms(true);
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleTranslatePrivacy = async () => {
    if (showTranslatedPrivacy) {
      setShowTranslatedPrivacy(false);
      return;
    }
    if (translatedPrivacyContent) {
      setShowTranslatedPrivacy(true);
      return;
    }
    try {
      setIsTranslating(true);
      const currentContent = langTab === 0 ? privacyContent : privacyContentFr;
      const currentTitle = langTab === 0 ? privacyTitle : privacyTitleFr;
      const [contentRes, titleRes] = await Promise.all([
        apiService.translateText(currentContent, targetLang.code),
        apiService.translateText(currentTitle, targetLang.code)
      ]);
      if (contentRes.success) setTranslatedPrivacyContent(contentRes.data.translatedText);
      if (titleRes.success) setTranslatedPrivacyTitle(titleRes.data.translatedText);
      setShowTranslatedPrivacy(true);
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleLanguageSelect = (lang: any) => {
    setTargetLang(lang);
    setLangModalVisible(false);
    setTranslatedTermsContent('');
    setTranslatedPrivacyContent('');
    setShowTranslatedTerms(false);
    setShowTranslatedPrivacy(false);
  };

  const formatContent = (text: string) => {
    if (!text) return null;
    const hasHTML = /<[a-z][\s\S]*>/i.test(text);
    const contentStyles = {
      '& *': { fontFamily: 'inherit !important' },
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        fontWeight: '600 !important',
        mt: 4,
        mb: 2,
        color: '#0f172a !important',
        lineHeight: 1.3,
      },
      '& h1': { fontSize: '4rem !important' },
      '& h2': { fontSize: '3.5rem !important' },
      '& h3': { fontSize: '3rem !important' },
      '& h4': { fontSize: '2.5rem !important' },
      '& h5': { fontSize: '2.25rem !important' },
      '& h6': { fontSize: '2rem !important' },
      '& p': {
        fontSize: '2.25rem !important',
        lineHeight: 1.8,
        color: '#334155 !important',
        mb: 2,
        marginTop: 0,
      },
      '& ul, & ol': { pl: 3, mb: 2, marginTop: 0 },
      '& li': {
        fontSize: '2.25rem !important',
        lineHeight: 1.8,
        color: '#334155 !important',
        mb: 1.5,
      },
      '& strong, & b': {
        fontWeight: '600 !important',
        color: '#0f172a !important',
      },
      '& em, & i': { fontStyle: 'italic !important' },
      '& code': {
        backgroundColor: '#f1f5f9 !important',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '1.8em !important',
        fontFamily: 'monospace !important',
        color: '#334155 !important',
      },
      '& pre': {
        backgroundColor: '#f1f5f9 !important',
        padding: '16px',
        borderRadius: '8px',
        overflow: 'auto',
        mb: 2,
        '& code': { backgroundColor: 'transparent !important', padding: 0 },
      },
      '& blockquote': {
        borderLeft: '4px solid #3b82f6 !important',
        pl: 2,
        ml: 0,
        fontStyle: 'italic',
        color: '#64748b !important',
        mb: 2,
      },
      '& a': {
        color: '#3b82f6 !important',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      },
    };
    if (hasHTML) {
      return (
        <Box
          className="content-wrapper"
          sx={contentStyles}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      );
    }
    return (
      <Box className="content-wrapper" sx={contentStyles}>
        <ReactMarkdown>{text}</ReactMarkdown>
      </Box>
    );
  };




  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0f172a',
        color: '#ffffff',
        py: { xs: 6, md: 8 },
        mt: 'auto',
        width: '100%',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Ride Club Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/Pic/Logo.svg"
                  alt="Ride Club Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '2.25rem',
                  color: '#ffffff',
                }}
              >
                Ride Club
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#9ca3af',
                mb: 3,
                lineHeight: 1.6,
                fontSize: '1.3125rem',
              }}
            >
              {t('footer.brandDescription')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton
                href="#"
                component="a"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.5rem',
                  '&:hover': {
                    color: '#10b981',
                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Twitter fontSize="inherit" />
              </IconButton>
              <IconButton
                href="#"
                component="a"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.5rem',
                  '&:hover': {
                    color: '#10b981',
                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Instagram fontSize="inherit" />
              </IconButton>
              <IconButton
                href="#"
                component="a"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.5rem',
                  '&:hover': {
                    color: '#10b981',
                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <LinkedIn fontSize="inherit" />
              </IconButton>
            </Box>
          </Grid>

          {/* Company Section */}
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.3125rem',
                color: '#ffffff',
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('footer.company')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                onClick={() => setAboutModalOpen(true)}
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    color: '#10b981',
                    backgroundColor: 'transparent',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.aboutUs')}
              </Button>
              <Button
                onClick={handleTermsClick}
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    color: '#10b981',
                    backgroundColor: 'transparent',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.termsOfService')}
              </Button>
              <Button
                onClick={() => setSafetyModalOpen(true)}
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    color: '#10b981',
                    backgroundColor: 'transparent',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.safetyFirst')}
              </Button>
              <Link
                href="#"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#10b981',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.pressRoom')}
              </Link>
            </Box>
          </Grid>

          {/* Support Section */}
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.3125rem',
                color: '#ffffff',
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('footer.support')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link
                component={RouterLink}
                to="/help"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#10b981',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.helpCenter')}
              </Link>
              <Link
                component={RouterLink}
                to="/help"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#10b981',
                  },
                  transition: 'color 0.2s',
                  fontWeight: 'bold',
                }}
              >
                FAQ
              </Link>
              <Button
                onClick={handlePrivacyClick}
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    color: '#10b981',
                    backgroundColor: 'transparent',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.privacyPolicy')}
              </Button>
              <Button
                onClick={handleTermsClick}
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    color: '#10b981',
                    backgroundColor: 'transparent',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.termsOfService')}
              </Button>
              <Link
                component={RouterLink}
                to="/contact"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#10b981',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.contactUs')}
              </Link>
              <Link
                href="#"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#10b981',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.communityGuidelines')}
              </Link>
              <Link
                href="#"
                sx={{
                  color: '#9ca3af',
                  fontSize: '1.3125rem',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#10b981',
                  },
                  transition: 'color 0.2s',
                }}
              >
                {t('footer.links.supportStatus')}
              </Link>
            </Box>
          </Grid>

          {/* Get the App Section */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.3125rem',
                color: '#ffffff',
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('footer.getTheApp')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: '200px' }}>
              <Button
                variant="contained"
                startIcon={<AppleIcon sx={{ color: '#0f172a' }} />}
                sx={{
                  bgcolor: '#ffffff',
                  color: '#0f172a',
                  py: 1.5,
                  px: 2,
                  borderRadius: '8px',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}>
                  <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1, color: '#64748b' }}>{t('footer.downloadOn')}</Typography>
                  <Typography sx={{ fontSize: '1.3125rem', fontWeight: 600, lineHeight: 1.2, color: '#0f172a' }}>{t('footer.appStore')}</Typography>
                </Box>
              </Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#ffffff',
                  color: '#0f172a',
                  py: 1.5,
                  px: 2,
                  borderRadius: '8px',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: '#f1f5f9',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#0f172a">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L14.54,17.15L6.05,2.66L20.16,10.81Z" />
                  </svg>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1, color: '#64748b' }}>{t('footer.getItOn')}</Typography>
                  <Typography sx={{ fontSize: '1.3125rem', fontWeight: 600, lineHeight: 1.2, color: '#0f172a' }}>{t('footer.googlePlay')}</Typography>
                </Box>
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {/* Bottom Section with Privacy Policy and Terms */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: '1.3125rem',
            }}
          >
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Button
              onClick={handlePrivacyClick}
              sx={{
                color: '#9ca3af',
                fontSize: '1.3125rem',
                textDecoration: 'none',
                textTransform: 'none',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  color: '#10b981',
                  backgroundColor: 'transparent',
                },
                transition: 'color 0.2s',
              }}
            >
              {t('footer.links.privacyPolicy')}
            </Button>
            <Button
              onClick={handleTermsClick}
              sx={{
                color: '#9ca3af',
                fontSize: '1.3125rem',
                textDecoration: 'none',
                textTransform: 'none',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  color: '#10b981',
                  backgroundColor: 'transparent',
                },
                transition: 'color 0.2s',
              }}
            >
              {t('footer.links.termsOfService')}
            </Button>
            <Button
              onClick={() => setSafetyModalOpen(true)}
              sx={{
                color: '#9ca3af',
                fontSize: '1.3125rem',
                textDecoration: 'none',
                textTransform: 'none',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  color: '#10b981',
                  backgroundColor: 'transparent',
                },
                transition: 'color 0.2s',
              }}
            >
              {t('footer.links.safety')}
            </Button>
          </Box>
        </Box>
      </Container>

      {/* About Us Modal */}
      <Dialog
        open={aboutModalOpen}
        onClose={() => setAboutModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
            pb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('footer.links.aboutUs')}
          </Typography>
          <IconButton
            onClick={() => setAboutModalOpen(false)}
            sx={{ color: '#6b7280' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '8px',
              backgroundColor: '#ffffff',
            }}
          >
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
              {formatContent(t('aboutUsModal.content'))}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={() => setAboutModalOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {t('footer.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terms of Service Modal */}
      <Dialog
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
            pb: 2,
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, flexShrink: 0 }}>
              {showTranslatedTerms ? translatedTermsTitle : (langTab === 0 ? termsTitle : termsTitleFr)}
            </Typography>
            <Tabs value={langTab} onChange={(e, v) => setLangTab(v)} sx={{ ml: 2 }}>
              <Tab label="EN" sx={{ minWidth: 50 }} />
              <Tab label="FR" sx={{ minWidth: 50 }} />
            </Tabs>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setLangModalVisible(true)}
              startIcon={<TranslateIcon />}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              {targetLang.flag} {targetLang.name}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleTranslateTerms}
              disabled={isTranslating}
              startIcon={showTranslatedTerms ? <HistoryIcon /> : <TranslateIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #3B82F6 100%)',
              }}
            >
              {isTranslating ? '...' : (showTranslatedTerms ? t('common.showOriginal') : t('common.translate'))}
            </Button>
            <IconButton
              onClick={() => setTermsModalOpen(false)}
              sx={{ color: '#6b7280' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: '12px', fontSize: '2rem', fontWeight: '700' }}>
            🛡️ {t('termsPage.legalDisclaimer', 'This translation is for convenience. The English version is the legal standard.')}
          </Alert>
          {loadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
              }}
            >
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                {showTranslatedTerms ? (
                  formatContent(translatedTermsContent)
                ) : (
                  langTab === 0 ? (
                    termsContent ? formatContent(termsContent) : <Typography>{t('common.contentNotAvailable')}</Typography>
                  ) : (
                    termsContentFr ? formatContent(termsContentFr) : <Typography>{t('common.contentNotAvailableFr', 'Contenu non disponible en français')}</Typography>
                  )
                )}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={() => setTermsModalOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Policy Modal */}
      <Dialog
        open={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
            pb: 2,
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, flexShrink: 0 }}>
              {showTranslatedPrivacy ? translatedPrivacyTitle : (langTab === 0 ? privacyTitle : privacyTitleFr)}
            </Typography>
            <Tabs value={langTab} onChange={(e, v) => setLangTab(v)} sx={{ ml: 2 }}>
              <Tab label="EN" sx={{ minWidth: 50 }} />
              <Tab label="FR" sx={{ minWidth: 50 }} />
            </Tabs>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setLangModalVisible(true)}
              startIcon={<TranslateIcon />}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              {targetLang.flag} {targetLang.name}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleTranslatePrivacy}
              disabled={isTranslating}
              startIcon={showTranslatedPrivacy ? <HistoryIcon /> : <TranslateIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #3B82F6 100%)',
              }}
            >
              {isTranslating ? '...' : (showTranslatedPrivacy ? t('common.showOriginal') : t('common.translate'))}
            </Button>
            <IconButton
              onClick={() => setPrivacyModalOpen(false)}
              sx={{ color: '#6b7280' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: '12px', fontSize: '2rem', fontWeight: '700' }}>
            🛡️ {t('termsPage.legalDisclaimer', 'This translation is for convenience. The English version is the legal standard.')}
          </Alert>
          {loadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
              }}
            >
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                {showTranslatedPrivacy ? (
                  formatContent(translatedPrivacyContent)
                ) : (
                  langTab === 0 ? (
                    privacyContent ? formatContent(privacyContent) : <Typography>{t('common.contentNotAvailable')}</Typography>
                  ) : (
                    privacyContentFr ? formatContent(privacyContentFr) : <Typography>{t('common.contentNotAvailableFr', 'Contenu non disponible en français')}</Typography>
                  )
                )}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={() => setPrivacyModalOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Safety First Modal */}
      <Dialog
        open={safetyModalOpen}
        onClose={() => setSafetyModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
            pb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('footer.links.safetyFirst')}
          </Typography>
          <IconButton
            onClick={() => setSafetyModalOpen(false)}
            sx={{ color: '#6b7280' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '8px',
              backgroundColor: '#ffffff',
            }}
          >
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
              {formatContent(t('safetyModal.content'))}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={() => setSafetyModalOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {t('footer.close')}
          </Button>
        </DialogActions>
      </Dialog>
      <LanguageModal
        open={langModalVisible}
        onClose={() => setLangModalVisible(false)}
        onSelect={handleLanguageSelect}
        title={t('navbar.language', 'Select Language')}
        selectedCode={targetLang.code}
      />
    </Box >
  );
}
